import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup, {
  RadioGroupItem,
} from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik } from "formik";
import { useToast } from "hooks/useToast";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  AccessLevel,
  Permission,
  PermissionCategory,
  PermissionItem,
  RoleDetail,
  useCreateRoleMutation,
  useGetPermissionStructureQuery,
  useUpdateRoleMutation,
} from "store/apis/roles";
import classNames from "classnames";
import { SettingsAccordion } from "pages/Books/Settings/SettingsAccordion";
import * as Yup from "yup";

type FormValues = {
  name: string;
  permissions: Record<string, AccessLevel>;
};

const validationSchema = Yup.object({
  name: Yup.string().required("Role name is required"),
});

const DEPENDENCY_MAP: Record<string, string[]> = {
  TRANSACTIONS: ["DATA_SOURCES"],
  FINANCIALS: [
    "DATA_SOURCES",
    "TRANSACTIONS",
    "GENERAL_LEDGER",
    "CHART_OF_ACCOUNTS",
    "JOURNAL_ENTRIES",
  ],
  ACCOUNTING: ["DATA_SOURCES", "TRANSACTIONS", "FINANCIALS"],
};

const REVERSE_DEPENDENCY_MAP: Record<string, string[]> = {
  DATA_SOURCES: ["TRANSACTIONS", "FINANCIALS", "ACCOUNTING"],
  TRANSACTIONS: ["FINANCIALS", "ACCOUNTING"],
  GENERAL_LEDGER: ["FINANCIALS"],
  CHART_OF_ACCOUNTS: ["FINANCIALS"],
  JOURNAL_ENTRIES: ["FINANCIALS"],
  FINANCIALS: ["ACCOUNTING"],
};

const REQUIRED_BY_LABELS: Record<string, string> = {
  DATA_SOURCES: "Transactions",
  TRANSACTIONS: "Financials, Accounting",
  GENERAL_LEDGER: "Financials",
  CHART_OF_ACCOUNTS: "Financials",
  JOURNAL_ENTRIES: "Financials",
  FINANCIALS: "Accounting",
};

const ACCESS_LEVEL_RANK: Record<AccessLevel, number> = {
  NO_ACCESS: 0,
  READ_ONLY: 1,
  FULL: 2,
};

function withDependencies(
  permissions: Record<string, AccessLevel>,
  key: string,
  level: AccessLevel
): Record<string, AccessLevel> {
  const updated = { ...permissions };
  updated[key] = level;

  if (level === "NO_ACCESS") {
    for (const dep of REVERSE_DEPENDENCY_MAP[key] || []) {
      updated[dep] = "NO_ACCESS";
    }
  } else {
    for (const dep of DEPENDENCY_MAP[key] || []) {
      const currentDepLevel = updated[dep] || "NO_ACCESS";
      if (ACCESS_LEVEL_RANK[currentDepLevel] < ACCESS_LEVEL_RANK[level]) {
        updated[dep] = level;
      }
    }
  }

  return updated;
}

export const CreateEditRoleModal = ({
  open,
  onClose,
  role,
  readOnly = false,
  userCount,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  role?: RoleDetail | null;
  readOnly?: boolean;
  userCount?: number;
  onDelete?: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const [createRole, { isLoading: creating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateRoleMutation();
  const { data: permissionStructure = [] } = useGetPermissionStructureQuery(
    { groupId: groupId! },
    { skip: !groupId }
  );

  const isEdit = Boolean(role) && !readOnly;
  const isLoading = creating || updating;

  const initialPermissions: Record<string, AccessLevel> = {};
  if (role?.permissions) {
    for (const p of role.permissions) {
      initialPermissions[p.permission_key] = p.access_level;
    }
  }

  const handleSubmit = async (values: FormValues) => {
    const allKeys = permissionStructure.flatMap((cat: PermissionCategory) =>
      cat.permissions.map((p: PermissionItem) =>
        p.name.toUpperCase().replace(/ /g, "_")
      )
    );

    const permissions: Permission[] = allKeys.map((key: string) => ({
      permission_key: key,
      access_level: values.permissions[key] ?? "NO_ACCESS",
    }));

    try {
      if (isEdit && role) {
        await updateRole({
          groupId: groupId!,
          roleId: role.uuid,
          name: values.name,
          permissions,
        }).unwrap();
        successToast({ message: "Role updated successfully" });
      } else {
        await createRole({
          groupId: groupId!,
          name: values.name,
          permissions,
        }).unwrap();
        successToast({ message: "Role created successfully" });
      }
      onClose();
    } catch (err: any) {
      alertToast({
        message: err?.data?.error?.message || "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>
            {readOnly ? "View Role" : isEdit ? "Edit Role" : "Create role"}
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Formik
          initialValues={{
            name: role?.name || "",
            permissions: initialPermissions,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="all:unset">
              <Modal.Body className="t-flex t-flex-col t-gap-5 t-overflow-y-auto t-max-h-[60vh]">
                <TextInput
                  label="Role name"
                  name="name"
                  placeholder="Enter"
                  required
                  disabled={readOnly}
                />
                <div className="t-flex t-flex-col t-gap-2">
                  <div className="t-text-body t-text-text-60 t-font-medium">
                    Select permissions
                  </div>
                  <SettingsAccordion.Root
                    key={permissionStructure[0]?.category}
                    defaultValue={
                      permissionStructure[0]
                        ? [permissionStructure[0].category]
                        : []
                    }
                  >
                    {permissionStructure.map((category: PermissionCategory) => (
                      <SettingsAccordion.Item
                        key={category.category}
                        value={category.category}
                      >
                        <SettingsAccordion.Trigger>
                          <span className="t-text-body t-font-semibold t-text-text-100">
                            {category.category}
                          </span>
                        </SettingsAccordion.Trigger>
                        <SettingsAccordion.Content className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-overflow-hidden t-mb-2 t-m-4">
                          <div className="t-grid t-px-4 t-py-2 t-bg-surface-lighter-grey t-grid-cols-[1fr_80px_80px_80px]">
                            <span className="t-text-body-sm t-text-text-30 t-font-medium">
                              Permission
                            </span>
                            <span className="t-text-body-sm t-text-text-30 t-font-medium t-text-center">
                              Full
                            </span>
                            <span className="t-text-body-sm t-text-text-30 t-font-medium t-text-center">
                              Read-only
                            </span>
                            <span className="t-text-body-sm t-text-text-30 t-font-medium t-text-center">
                              No Access
                            </span>
                          </div>
                          {category.permissions.map((perm) => {
                            const key = perm.name
                              .toUpperCase()
                              .replace(/ /g, "_");
                            const currentLevel = values.permissions[key];
                            const isNoAccess =
                              !currentLevel || currentLevel === "NO_ACCESS";
                            return (
                              <div
                                key={perm.name}
                                className="t-grid t-grid-cols-[1fr_80px_80px_80px] t-items-start t-px-4 t-py-3 t-bg-white"
                              >
                                <div
                                  className={classNames({
                                    "t-opacity-40": isNoAccess,
                                  })}
                                >
                                  <div className="t-text-body t-font-medium t-text-text-100">
                                    {perm.name}
                                  </div>
                                  {perm.description && (
                                    <div className="t-text-body-sm t-text-text-30 t-mt-0.5">
                                      {perm.description}
                                    </div>
                                  )}
                                  {REQUIRED_BY_LABELS[key] && (
                                    <span className="t-mt-1 t-inline-block t-w-fit t-rounded-full t-bg-blue-10 t-px-2 t-py-0.5 t-text-body-sm t-font-medium t-text-blue-80">
                                      Required by {REQUIRED_BY_LABELS[key]}
                                    </span>
                                  )}
                                </div>
                                <RadioGroup.Root
                                  value={currentLevel || "NO_ACCESS"}
                                  disabled={readOnly}
                                  onValueChange={(value) => {
                                    if (readOnly) return;
                                    setFieldValue(
                                      "permissions",
                                      withDependencies(
                                        values.permissions,
                                        key,
                                        value as AccessLevel
                                      )
                                    );
                                  }}
                                  className="t-contents"
                                >
                                  <div className="t-flex t-justify-center t-pt-0.5">
                                    <RadioGroupItem value="FULL" />
                                  </div>
                                  <div className="t-flex t-justify-center t-pt-0.5">
                                    <RadioGroupItem value="READ_ONLY" />
                                  </div>
                                  <div className="t-flex t-justify-center t-pt-0.5">
                                    <RadioGroupItem value="NO_ACCESS" />
                                  </div>
                                </RadioGroup.Root>
                              </div>
                            );
                          })}
                        </SettingsAccordion.Content>
                      </SettingsAccordion.Item>
                    ))}
                  </SettingsAccordion.Root>
                </div>
              </Modal.Body>
              <Modal.Footer className="t-flex t-items-center t-justify-between">
                <div>
                  {isEdit && onDelete && userCount === 0 && (
                    <Button
                      type="button"
                      customType="danger_link"
                      onClick={onDelete}
                    >
                      Delete role
                    </Button>
                  )}
                </div>
                <div className="t-flex t-gap-3">
                  {readOnly ? (
                    <Button type="button" onClick={onClose}>
                      Close
                    </Button>
                  ) : (
                    <>
                      <Button type="button" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        customType="primary"
                        type="submit"
                        isLoading={isLoading}
                        disabled={isLoading}
                      >
                        {isEdit ? "Save Changes" : "Create"}
                      </Button>
                    </>
                  )}
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
